<template>
  <section class="container ccpa-container">
    <div class="row">
      <div v-if="optedOut" class="banner banner--small banner--green col s12 l6 offset-l3">
        <p class="banner__text banner__text--left">You have opted out on {{ date }}</p>
      </div>
      <div v-if="errorBanner" class="banner banner--small banner--red col s12 l6 offset-l3">
        <p id="error-banner" class="banner__text banner__text--left">
          There was an error in updating your preference. Please try again.
        </p>
      </div>
      <form class="form-card col s12 l6 offset-l3" @submit.prevent="submit">
        <h1 class="ccpa-card col s12 heading-3 xs-vertical-spacing">Do not sell my personal information</h1>
        <p class="col s12 body-2 xs-vertical-spacing">
          At Sittercity, we honor the importance of personal information. That's why:
        </p>
        <ul class="col s11 offset-s1 l11 offset-l1 list-group sm-vertical-spacing">
          <li class="list-group__item list-group--bullet body-2">We're not selling any personal information</li>
          <li class="list-group__item list-group--bullet body-2">We don't have plans to sell personal information</li>
        </ul>
        <p class="col s12 body-2 sm-vertical-spacing">
          For more information, read our <a href="https://www.sittercity.com/privacy"><u>Privacy Policy</u></a> or this
          <a href="https://support.sittercity.com/hc/en-us/articles/360040666454-Does-Sittercity-sell-my-personal-information"
            ><u>Help Center article</u></a
          >
          about personal data. We respect and understand that you may wish to ensure that your personal information isn't sold in
          the future.
        </p>
        <p v-if="!optedOut" class="col s12 body-2 sm-vertical-spacing">If so, please update your status below.</p>

        <div v-if="!optedOut">
          <div class="col s12 sm-vertical-spacing">
            <poppins-checkbox
              v-model="form.opt_out"
              checkbox-id="form-not"
              labelName="Do Not Sell My Personal Information"
              :status="formStatuses.opt_out"
              :helperText="helperText(formStatuses.opt_out, 'opt_out', form.opt_out)"
              @blur="validateOptOut"
            />
          </div>

          <div class="col s12 m4 l3">
            <poppins-button value="submit" variant="primary" size="medium" class="w-full mb-24" />
          </div>
        </div>
      </form>
    </div>
  </section>
</template>

<script lang="ts">
import get from 'lodash/get';
import helperStatus from '../../helpers/status';
import Checkbox from '@/components/checkbox/Checkbox.vue';
import Button from '@/components/button/Button.vue';
import { CREATE_DSR_REQUEST } from '../../graphql/dsr/mutations';
import { DSR_REQUESTS } from '../../graphql/dsr/queries';

export default {
  name: 'DoNotSellFormLoggedIn',
  components: {
    PoppinsCheckbox: Checkbox,
    PoppinsButton: Button,
  },
  data(): any {
    return {
      form: {
        opt_out: false,
      },
      formStatuses: {
        opt_out: helperStatus.Neutral,
      },
      requiredFields: ['opt_out'],
      success: false,
      dsrRequests: null,
      errorBanner: false,
      apolloReady: false,
      apolloCheckInterval: null,
    };
  },
  computed: {
    date(): string {
      if (this.success) {
        return new Date().toLocaleDateString('en-us', { month: 'long', day: 'numeric', year: 'numeric' });
      }
      const recentDsrRequest = get(this.dsrRequests, 'edges[0].node');
      if (recentDsrRequest) {
        return new Date(recentDsrRequest.createdAt).toLocaleDateString('en-us', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });
      }
      return '';
    },
    optedOut(): boolean {
      const recentDsrRequest = get(this.dsrRequests, 'edges[0].node');
      return this.success || !!recentDsrRequest;
    },
  },
  apollo: {
    dsrRequests: {
      query: DSR_REQUESTS,
      skip() {
        return !this.apolloReady;
      },
    },
  },
  watch: {
    form: {
      handler() {
        Object.keys(this.formStatuses).forEach((key) => {
          if (this.formStatuses[key] !== helperStatus.Error) {
            this.formStatuses[key] = helperStatus.Neutral;
          }
        });
      },
      deep: true,
    },
    'form.opt_out': function optOutHandler(newValue) {
      if (newValue) {
        this.formStatuses.opt_out = helperStatus.Neutral;
      }
    },
  },
  mounted() {
    this.checkApolloReady();
  },
  beforeDestroy() {
    if (this.apolloCheckInterval) {
      clearInterval(this.apolloCheckInterval);
    }
  },
  methods: {
    checkApolloReady() {
      const maxRetries = 10;
      let retryCount = 0;

      this.apolloCheckInterval = setInterval(() => {
        if (this.$apollo && this.$apollo.provider) {
          this.apolloReady = true;
          clearInterval(this.apolloCheckInterval);
          this.apolloCheckInterval = null;
        } else {
          retryCount += 1;
          if (retryCount >= maxRetries) {
            this.apolloReady = true;
            clearInterval(this.apolloCheckInterval);
            this.apolloCheckInterval = null;
            console.warn('Apollo provider not detected after retries, proceeding anyway');
          }
        }
      }, 100);
    },
    validateRequiredFields(fieldName, fieldValue) {
      if (this.fieldIsRequired(fieldName) && this.fieldIsEmpty(fieldValue)) {
        this.formStatuses[fieldName] = helperStatus.Error;
      }
    },
    validateOptOut() {
      if (this.form.opt_out === false) {
        this.formStatuses.opt_out = helperStatus.Error;
      }
    },
    fieldIsEmpty(fieldValue) {
      return fieldValue.length === 0;
    },
    fieldIsRequired(fieldName) {
      return this.requiredFields.includes(fieldName);
    },
    helperText(status: helperStatus, type: string) {
      if (status === helperStatus.Error) {
        if (this.requiredFields.includes(type)) {
          return 'This field is required';
        }
      }
      return '';
    },
    canSubmit() {
      const newErrors = {};

      this.validateOptOut();

      Object.entries(this.form).forEach((e) => {
        this.validateRequiredFields(e[0], e[1]);
      });

      Object.keys(this.formStatuses).forEach((key) => {
        if (this.formStatuses[key] === helperStatus.Error) {
          const helperText = 'This field is required';
          const status = helperStatus.Error;
          newErrors[key] = { helperText, status };
        }
      });

      this.errors = newErrors;

      if (Object.keys(newErrors).length > 0) {
        return false;
      }
      return true;
    },
    submit() {
      if (this.canSubmit()) return this.submitDoNotSellForm();
      return false;
    },

    async submitDoNotSellForm() {
      this.showLoader = true;
      try {
        await this.$apollo.mutate({
          mutation: CREATE_DSR_REQUEST,
          variables: {
            optOut: this.form,
          },
        });

        this.success = true;
      } catch (error) {
        console.error(error);
        this.errorBanner = true;
      }
    },
  },
};
</script>
